import { useEffect, useState } from "react";
import { Crown, Mail, Linkedin, Globe, Facebook, Instagram } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

/**
 * /board — Dedicated public page for Nail Viet Club Board of Directors.
 *
 * Premium layout: hero band → alternating left/right portrait + bio rows →
 * footer.  Pulls /api/board-of-directors and degrades cleanly to an empty
 * state while loading.
 */
export default function BoardOfDirectorsPage() {
  const { lang } = useI18n();
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/board-of-directors")
      .then((r) => { if (alive) setDirectors(r.data?.directors || []); })
      .catch(() => { if (alive) setDirectors([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-charcoal" data-testid="board-page">
      <SiteHeader variant="light" />

      <main>
        {/* Hero band */}
        <section className="bg-charcoal-900 dark-surface relative overflow-hidden">
          <div className="absolute inset-0 summit-gradient opacity-90" />
          <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold-500/40 bg-white/5">
              <Crown className="w-3.5 h-3.5 text-gold-400" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-gold-300 font-semibold">
                {lang === "en" ? "Platform Governance" : "Hội đồng Quản trị"}
              </span>
            </div>
            <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-tight">
              {lang === "en"
                ? "Board of Directors"
                : "Hội đồng Quản trị Nail Viet Club"}
            </h1>
            <p className="mt-5 text-white/80 text-lg max-w-3xl leading-relaxed">
              {lang === "en"
                ? "The leaders steering Nail Viet Club — chosen for their expertise in finance, community, technology, and operations across the Vietnamese nail industry in the United States."
                : "Đội ngũ định hướng Nail Viet Club — được chọn vì chuyên môn về tài chính, cộng đồng, công nghệ và vận hành trong ngành nail Việt tại Hoa Kỳ."}
            </p>
          </div>
        </section>

        {/* Director rows */}
        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 space-y-20 md:space-y-28">
            {loading && directors.length === 0 && (
              <div className="text-center text-warmgray-500 text-sm py-12" data-testid="board-loading">
                {lang === "en" ? "Loading…" : "Đang tải…"}
              </div>
            )}
            {!loading && directors.length === 0 && (
              <div className="text-center text-warmgray-500 text-sm py-12" data-testid="board-empty">
                {lang === "en" ? "No directors yet." : "Chưa có thành viên."}
              </div>
            )}
            {directors.map((d, idx) => {
              const reverse = idx % 2 === 1;
              return (
                <article
                  key={d.id}
                  data-testid={`board-director-row-${d.slug}`}
                  className={`grid md:grid-cols-12 gap-8 md:gap-12 items-center ${reverse ? "md:[direction:rtl]" : ""}`}
                >
                  <div className="md:col-span-5 md:[direction:ltr]">
                    <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-warmgray-100 shadow-md">
                      {d.photo_url ? (
                        <img
                          src={d.photo_url}
                          alt={d.full_name}
                          loading="lazy"
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-warmgray-400">
                          <Crown className="w-12 h-12" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-7 md:[direction:ltr]">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald2/10 text-emerald2">
                      <Crown className="w-3 h-3" />
                      <span className="text-[10px] uppercase tracking-[0.24em] font-semibold">
                        {d.board_title || (lang === "en" ? "Director" : "Thành viên HĐQT")}
                      </span>
                    </div>
                    <h2 className="mt-3 font-serif text-4xl md:text-5xl text-charcoal leading-tight">
                      {d.full_name}
                    </h2>
                    {d.title && (
                      <p className="mt-2 text-warmgray-700 text-lg">{d.title}</p>
                    )}
                    {d.company && d.company !== d.title && (
                      <p className="mt-0.5 text-sm text-emerald2 font-medium">{d.company}</p>
                    )}
                    {d.bio && (
                      <p className="mt-5 text-warmgray-700 leading-relaxed whitespace-pre-line">
                        {d.bio}
                      </p>
                    )}
                    {Array.isArray(d.topics) && d.topics.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        {d.topics.map((t) => (
                          <span
                            key={t}
                            className="px-3 py-1 rounded-full bg-warmgray-100 text-warmgray-700 text-xs"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      {d.website_url && (
                        <a href={d.website_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-emerald2 hover:underline"
                          data-testid={`board-website-${d.slug}`}>
                          <Globe className="w-4 h-4" /> Website
                        </a>
                      )}
                      {d.linkedin_url && (
                        <a href={d.linkedin_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-emerald2 hover:underline"
                          data-testid={`board-linkedin-${d.slug}`}>
                          <Linkedin className="w-4 h-4" /> LinkedIn
                        </a>
                      )}
                      {d.facebook_url && (
                        <a href={d.facebook_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-emerald2 hover:underline"
                          data-testid={`board-facebook-${d.slug}`}>
                          <Facebook className="w-4 h-4" /> Facebook
                        </a>
                      )}
                      {d.instagram_url && (
                        <a href={d.instagram_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-emerald2 hover:underline"
                          data-testid={`board-instagram-${d.slug}`}>
                          <Instagram className="w-4 h-4" /> Instagram
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* CTA band */}
        <section className="bg-warmgray-50 border-t border-warmgray-200">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">
              {lang === "en"
                ? "Join the community our Board is building"
                : "Tham gia cộng đồng do Hội đồng Quản trị xây dựng"}
            </h2>
            <p className="mt-4 text-warmgray-700">
              {lang === "en"
                ? "Events, education, AI guidance, and modern tools — built for Vietnamese nail professionals."
                : "Sự kiện, đào tạo, cố vấn AI và công cụ hiện đại — dành riêng cho chủ tiệm nail Việt."}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <a href="/events" className="inline-flex items-center gap-2 bg-emerald2 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 transition-colors" data-testid="board-cta-events">
                {lang === "en" ? "Browse events" : "Xem sự kiện"}
              </a>
              <a href="mailto:hello@nailvietclub.com" className="inline-flex items-center gap-2 border border-warmgray-300 bg-white text-charcoal rounded-full px-5 py-2.5 text-sm font-medium hover:bg-warmgray-50 transition-colors" data-testid="board-cta-contact">
                <Mail className="w-4 h-4" /> {lang === "en" ? "Contact us" : "Liên hệ"}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
